import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { useLeadStore } from '../store/leadStore';
import { useUserStore } from '../store/userStore';
import { useAuthStore } from '../store/authStore';
import { SourceLinkPanel } from '../components/leads/SourceLinkPanel';
import {
  HEARD_ABOUT_US_OPTIONS,
  PRIMARY_INTEREST_OPTIONS,
  type LeadDiscardReason,
  type LeadSource,
  type LeadSubmissionListItem,
} from '../types/lead';

/**
 * Lead review queue (CRM-129) — the human gate between the public form and the
 * directory. Nothing captured publicly is a Person until it is approved here, so
 * spam and athlete submissions die as discarded pre-leads.
 */
const DISCARD_REASONS: { value: LeadDiscardReason; label: string }[] = [
  { value: 'spam', label: 'Spam' },
  { value: 'athlete', label: 'Athlete (not a buyer)' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'other', label: 'Other' },
];

const heardAboutUsLabel = (value: string | null) =>
  HEARD_ABOUT_US_OPTIONS.find((o) => o.value === value)?.label ?? value;

const primaryInterestLabel = (value: string | null) =>
  PRIMARY_INTEREST_OPTIONS.find((o) => o.value === value)?.label ?? value;

const contactLine = (s: LeadSubmissionListItem) => [s.email, s.phone].filter(Boolean).join(' · ');

const placeLine = (s: LeadSubmissionListItem) =>
  [s.orgName, [s.city, s.state].filter(Boolean).join(', '), s.zip].filter(Boolean).join(' · ');

function SubmissionCard({ submission }: { submission: LeadSubmissionListItem }) {
  const { approve, discard, sources, isSaving } = useLeadStore();
  const users = useUserStore((s) => s.users);
  const [assignToUserId, setAssignToUserId] = useState('');
  const [sourceId, setSourceId] = useState(submission.source?.id ?? '');
  const [discardReason, setDiscardReason] = useState<LeadDiscardReason>('spam');

  const dup = submission.duplicatePerson;

  const onApprove = async () => {
    try {
      await approve(submission.id, {
        assignToUserId: assignToUserId || null,
        sourceId: sourceId && sourceId !== submission.source?.id ? sourceId : null,
      });
      toast.success(dup ? 'Linked to the existing coach' : 'Lead approved');
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const onDiscard = async () => {
    try {
      await discard(submission.id, discardReason);
      toast.success('Lead discarded');
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            {submission.firstName} {submission.lastName ?? ''}
            {submission.title && (
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">{submission.title}</span>
            )}
          </h3>
          {contactLine(submission) && (
            <p className="text-sm text-gray-600 dark:text-gray-300">{contactLine(submission)}</p>
          )}
          {placeLine(submission) && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{placeLine(submission)}</p>
          )}
          {submission.sourcePath && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              from <span className="font-mono">{submission.sourcePath}</span>
            </p>
          )}
          {submission.primaryInterest && (
            <span className="mt-2 inline-block rounded-full bg-blast-50 px-2 py-0.5 text-xs font-medium text-blast-700
                             dark:bg-blast-950 dark:text-blast-300">
              {primaryInterestLabel(submission.primaryInterest)}
            </span>
          )}
        </div>
        <div className="text-right text-xs text-gray-500 dark:text-gray-400">
          <p>{new Date(submission.createdAt).toLocaleString()}</p>
          {submission.source ? (
            <p>Source: {submission.source.label}</p>
          ) : submission.sourceCodeRaw ? (
            <p>Source code: {submission.sourceCodeRaw} (unrecognized)</p>
          ) : null}
          {submission.repUser && <p>Rep link: {submission.repUser.displayName ?? submission.repUser.email}</p>}
          {submission.heardAboutUs && <p>Heard: {heardAboutUsLabel(submission.heardAboutUs)}</p>}
          {submission.sportName && <p>Sport: {submission.sportName}</p>}
        </div>
      </div>

      {/* What the coach typed after submitting — often who their rep is, so it
          belongs above the assignment controls rather than in a details panel. */}
      {submission.note && (
        <blockquote className="mt-3 border-l-4 border-blast-400 bg-blast-50 px-3 py-2 text-sm text-gray-800
                               dark:bg-gray-900 dark:text-gray-100">
          <span className="block text-xs font-semibold uppercase tracking-wide text-blast-700 dark:text-blast-300">
            They added
          </span>
          {submission.note}
        </blockquote>
      )}

      {dup && (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-200">
          Already in the CRM as{' '}
          <Link to={`/people/${dup.id}`} className="font-semibold underline">
            {dup.firstName} {dup.lastName}
          </Link>
          {dup.assignedUser && <> — assigned to {dup.assignedUser.displayName ?? dup.assignedUser.email}</>}
          {dup.isActive
            ? '. Approving links to that record instead of creating a second one.'
            : '. That record is archived; approving brings it back and links to it.'}
        </p>
      )}

      {submission.status === 'PENDING_REVIEW' ? (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400" htmlFor={`assign-${submission.id}`}>
              Assign to
            </label>
            <select
              id={`assign-${submission.id}`}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
              value={assignToUserId}
              onChange={(e) => setAssignToUserId(e.target.value)}
            >
              <option value="">Nobody yet</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.displayName ?? u.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400" htmlFor={`source-${submission.id}`}>
              Source
            </label>
            <select
              id={`source-${submission.id}`}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
            >
              <option value="">Unset</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>{s.label}{s.isActive ? '' : ' (inactive)'}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={isSaving}
            onClick={onApprove}
            className="rounded-md bg-blast-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {dup ? 'Approve & link' : 'Approve'}
          </button>
          <div className="ml-auto flex items-end gap-2">
            <select
              aria-label="Discard reason"
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
              value={discardReason}
              onChange={(e) => setDiscardReason(e.target.value as LeadDiscardReason)}
            >
              {DISCARD_REASONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={isSaving}
              onClick={onDiscard}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700
                         disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
            >
              Discard
            </button>
          </div>
        </div>
      ) : (
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          {submission.status === 'APPROVED' ? 'Approved' : `Discarded (${submission.discardReason})`}
          {submission.decidedBy && <> by {submission.decidedBy.displayName ?? submission.decidedBy.email}</>}
          {submission.resolvedPersonId && (
            <>
              {' — '}
              <Link to={`/people/${submission.resolvedPersonId}`} className="underline">view coach</Link>
            </>
          )}
        </p>
      )}
    </div>
  );
}

function SourceManager() {
  const { sources, createSource, updateSource, isSaving } = useLeadStore();
  const [label, setLabel] = useState('');
  const [linkSource, setLinkSource] = useState<LeadSource | null>(null);

  const onCreate = async () => {
    if (!label.trim()) return;
    try {
      await createSource(label.trim());
      setLabel('');
      toast.success('Source created');
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="font-semibold text-gray-900 dark:text-gray-100">Sources</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        One per event. &quot;Get link&quot; builds the rep&apos;s lead link with this event&apos;s
        <code> ?source=&lt;code&gt;</code> tag and a printable QR — no new form needed. A code never changes once
        created, so printed links keep working; deactivate a source to retire it from the public list.
      </p>

      <div className="mt-3 flex gap-2">
        <input
          className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
          placeholder="Glazier Clinic 2026 — Las Vegas"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <button
          type="button"
          disabled={isSaving || !label.trim()}
          onClick={onCreate}
          className="rounded-md bg-blast-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          Add
        </button>
      </div>

      <ul className="mt-3 divide-y divide-gray-100 dark:divide-gray-700">
        {sources.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-3 py-2 text-sm">
            <span className={s.isActive ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}>
              {s.label}
              <code className="ml-2 text-xs text-gray-500 dark:text-gray-400">?source={s.code}</code>
            </span>
            <span className="flex flex-shrink-0 items-center gap-3">
              {/* Retired sources still resolve inbound, but the tooling only hands out live ones. */}
              {s.isActive && (
                <button
                  type="button"
                  onClick={() => setLinkSource(s)}
                  className="text-xs font-medium text-blast-600 underline dark:text-blast-400"
                >
                  Get link
                </button>
              )}
              <button
                type="button"
                disabled={isSaving}
                onClick={() => updateSource(s.id, { isActive: !s.isActive })}
                className="text-xs font-medium text-blast-600 underline disabled:opacity-50 dark:text-blast-400"
              >
                {s.isActive ? 'Deactivate' : 'Reactivate'}
              </button>
            </span>
          </li>
        ))}
        {sources.length === 0 && (
          <li className="py-2 text-sm text-gray-500 dark:text-gray-400">No sources yet.</li>
        )}
      </ul>

      <SourceLinkPanel source={linkSource} isOpen={linkSource !== null} onClose={() => setLinkSource(null)} />
    </section>
  );
}

export function LeadQueue() {
  const {
    submissions,
    statusFilter,
    isLoading,
    error,
    fetchQueue,
    fetchCount,
    fetchSources,
    sources,
    filters,
    setFilters,
    clearFilters,
  } = useLeadStore();
  const users = useUserStore((s) => s.users);
  const fetchUsers = useUserStore((s) => s.fetchUsers);
  const hasAnyRole = useAuthStore((s) => s.hasAnyRole);
  const canManageSources = hasAnyRole('MANAGER');
  const hasActiveFilters = Object.keys(filters).length > 0;

  useEffect(() => {
    fetchQueue('PENDING_REVIEW');
    fetchCount();
    fetchSources(true);
    fetchUsers();
  }, [fetchQueue, fetchCount, fetchSources, fetchUsers]);

  return (
    <div className="mx-auto max-w-5xl space-y-4 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">Leads</h1>
        <div className="flex gap-2">
          {(['PENDING_REVIEW', 'APPROVED', 'DISCARDED'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => fetchQueue(s)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                statusFilter === s
                  ? 'bg-blast-50 text-blast-700 dark:bg-blast-950 dark:text-blast-300'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {s === 'PENDING_REVIEW' ? 'Needs review' : s === 'APPROVED' ? 'Approved' : 'Discarded'}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <select
            className="input"
            aria-label="Filter by source"
            value={filters.sourceId ?? ''}
            onChange={(e) => setFilters({ sourceId: e.target.value })}
          >
            <option value="">All sources</option>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
                {s.isActive ? '' : ' (inactive)'}
              </option>
            ))}
          </select>

          {statusFilter === 'APPROVED' && (
            <select
              className="input"
              aria-label="Filter by assigned rep"
              value={filters.assignedTo ?? ''}
              onChange={(e) => setFilters({ assignedTo: e.target.value })}
            >
              <option value="">All reps</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName ?? u.email}
                </option>
              ))}
            </select>
          )}

          <input
            type="date"
            className="input"
            aria-label="From date"
            value={filters.dateFrom ?? ''}
            max={filters.dateTo || undefined}
            onChange={(e) => setFilters({ dateFrom: e.target.value })}
          />
          <input
            type="date"
            className="input"
            aria-label="To date"
            value={filters.dateTo ?? ''}
            min={filters.dateFrom || undefined}
            onChange={(e) => setFilters({ dateTo: e.target.value })}
          />
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            className="mt-2 text-sm font-medium text-blast-600 dark:text-blast-400 underline"
            onClick={() => clearFilters()}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Source CRUD and its link tooling are MANAGER-only, matching /api/lead-sources. */}
      {canManageSources && <SourceManager />}

      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {isLoading && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}
      {!isLoading && submissions.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Nothing here.</p>
      )}

      <div className="space-y-3">
        {submissions.map((s) => (
          <SubmissionCard key={s.id} submission={s} />
        ))}
      </div>
    </div>
  );
}
